import { Component, ViewChild, ElementRef, Inject, PLATFORM_ID } from '@angular/core';

import { idleTimeoutService } from './core/services/idleTimeout.service';
import { ActivatedRoute, Router, NavigationEnd, NavigationStart, Event as RouterNavigationEvent } from '@angular/router';
import { CartRecoverService, RestaurantService } from './core';
import { Location, isPlatformBrowser } from '@angular/common';
import { LoadingSpinnerService } from './core/services/loadingSpinner.service';
import { NavService } from './core/services/nav.service';
import { TranslatePipe } from "@ngx-translate/core";
import { OrderService } from './core/services/order.service';
import { AfterPaymentService } from './core/services/afterPayment.service';
import { LocalStorageService } from './core/services/localStorage.service'
import { isDesignSystemBoot } from './shared/design-system-availability';
import { Subscription } from 'rxjs';
import { comPrazo } from './shared/utils/com-prazo';
// Namespace: o vigia é estado de MÓDULO (não passa por injeção de dependência,
// porque o cenário que ele mede é o Angular não ter subido). Chamar por
// `vigiaDeBoot.x()` deixa isso visível na call-site.
import * as vigiaDeBoot from './core/services/telemetry/boot-watchdog';
import { VersionCheckService } from './core/services/telemetry/version-check.service';
import { OrderFlowTraceService } from './core/services/telemetry/order-flow-trace.service';

/**
 * Prazo da consulta do último pedido no boot.
 *
 * Menor que o do cardápio porque o que está em jogo é menor: sem cardápio não há
 * tela, mas sem esta resposta só se perde a tela de avaliação. Vale esperar
 * pouco e seguir.
 */
export const PRAZO_ULTIMO_PEDIDO_MS = 6000;

/**
 * Prazo da NAVEGAÇÃO DE ENTRADA do boot.
 *
 * Medido em 01/09/2026 (Athena, `order_flow_monitor.traces`): 328 a 471
 * devices/dia emitem `ClientBootStalled` na fase `'primeira-tela'`, e ~71% deles
 * NUNCA veem o cardápio. Em 31/08, dos 471 travados só 14 (3%) emitiram
 * `ClientAppError` — os outros 334 ficaram em silêncio absoluto. Silêncio é a
 * assinatura de **promessa que não assenta**: uma que rejeita produz erro.
 *
 * E o `navService.push` do boot era o único `await` de navegação do app sem
 * prazo e sem `.catch()`. Pior: o `loadingSpinner.dismiss()` do início do
 * `ngOnInit` já rodou, então o spinner sai e nada entra no lugar — não é splash
 * eterno, é tela literalmente em branco. É 30 a 100× maior que o boot morto
 * (o `main.js` que nem executa, 3 a 11 devices/dia).
 *
 * ## Por que 10 s, e por que TEM de ser menor que o prazo do vigia
 *
 * `PRAZO_VIGIA_MS` é 20 s. Se este prazo fosse maior ou igual, o vigia
 * reportaria o travamento ANTES de o plano B agir: o cliente veria a tela branca
 * do mesmo jeito e o instrumento estaria medindo um defeito que já tinha cura
 * disponível e não aplicada. Com 10 s sobra a metade da janela — a recuperação
 * acontece primeiro, e o desfecho dela vira evento próprio
 * (`ClientBootNavFallback`, ver `reportarPlanoBDoBoot`).
 *
 * Maior que `PRAZO_ULTIMO_PEDIDO_MS` (6 s) porque o que está em jogo é maior:
 * lá se perde a tela de avaliação, aqui se perde a tela inteira. Em 3G ruim uma
 * rota lazy honesta ainda cabe em 10 s.
 */
export const PRAZO_NAVEGACAO_BOOT_MS = 10000;

/**
 * Destino do plano B.
 *
 * `/menu` é o único destino que sabe se explicar sem nada carregado:
 * `menu.page.ts` faz `if(!restaurant) { invalidURL = true }` e o template mostra
 * `<app-not-found>`, ou o convite a tentar de novo quando a carga estourou.
 * Entregar a tela errada é melhor do que não entregar tela.
 */
const ROTA_SEGURA_DE_BOOT = '/menu';

/**
 * Última chance dada à navegação antes de o app desenhar tela por conta própria.
 *
 * O plano B não consegue saber NA HORA se a navegação que ele disparou chegou:
 * o `push` dele é fire-and-forget de propósito — esperar de novo recriaria a
 * espera sem fim que esta correção conserta. Então espera-se um pouco e
 * pergunta-se ao roteador, que sabe responder: `router.navigated` só vira
 * `true` quando alguma navegação COMPLETA.
 *
 * 3 s porque a essa altura o cliente já está há 10 s no branco, e o que falta
 * medir é só o tempo de montar um componente cujo chunk, se ia chegar, já
 * chegou. Somados aos 10 s do plano B dá 13 s — ainda abaixo dos 20 s do vigia,
 * então quem é atendido aqui não conta como travado.
 */
export const PRAZO_ULTIMA_CHANCE_MS = 3000;

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss']
})

export class AppComponent {

	private restaurantUrl
	public isNewHomePedirDelivery: boolean = false;

	/**
	 * O boot terminou sem NENHUMA tela? (ver `verificarSeEntregouTela`)
	 *
	 * Liga o único bloco do `app.component.html` que não depende do roteador — e é
	 * exatamente por isso que ele existe. O template do shell é só
	 * `<ion-app>` + outlets: quando a navegação de entrada não chega, o outlet
	 * fica VAZIO e o `<ion-app>` é uma casca. Com o spinner já dispensado pelo
	 * plano B, o cliente olha branco puro — e o vigia foi desarmado, então nem
	 * alarme sobra.
	 *
	 * Medido em produção em 02/09/2026, na primeira hora da cura: 3 dos 6
	 * `ClientBootNavFallback` caíram no ramo `destino: 'nenhum'`, que assumia que
	 * "a tela do menu já está montada". Se o `push('/menu')` pendurou, ela não
	 * está.
	 */
	public bootSemTela = false;

	// Causa que acionou o plano B ('prazo' | 'rejeicao'), guardada para o evento da
	// tela de recuperação poder dizer de QUAL falha ele é a consequência — os dois
	// eventos são do mesmo passo e só se cruzam por este campo.
	private motivoDoPlanoB = '';

	// O usuário navegou por conta própria durante o boot? (ver vigiarNavegacaoDoUsuario)
	private houveNavegacaoDoUsuario = false;

	constructor(
				private idleService: idleTimeoutService,
				private restaurantService: RestaurantService,
				private router: Router,
				private location: Location,
				private loadingSpinner: LoadingSpinnerService,
				private navService: NavService,
				private activatedRoute: ActivatedRoute,
				public translatePipe: TranslatePipe,
                private afterPaymentService: AfterPaymentService,
                private orderService: OrderService,
                private localStorageService: LocalStorageService,
                private cartRecoverService: CartRecoverService,
                private versionCheckService: VersionCheckService,
                @Inject(PLATFORM_ID) private platformId: Object,
                // Último parâmetro de propósito: os specs constroem o componente
                // por posição, e acrescentar no fim não desloca nenhum argumento
                // já escrito. Só o plano B da navegação de entrada o usa.
                //
                // Tipado sem `?` porque o gate `check:telemetry-coverage` resolve
                // o receptor pelo TIPO declarado (`nome: OrderFlowTraceService`);
                // com `?` a regra não casa e o emissor viraria invisível para o
                // gate — que é exatamente a cegueira que ele existe para pegar.
                // A ausência em teste é tratada em `reportarPlanoBDoBoot`.
                private orderFlowTrace: OrderFlowTraceService,
	) {	}

	async ngOnInit() {
		// Catálogo do design system (só local): não há restaurante carregado, e todo o
		// fluxo abaixo — carrinho, último pedido, redirect para /menu — pressupõe um.
		if (isDesignSystemBoot()) {
			this.loadingSpinner.dismiss();
			return;
		}

		this.isNewHomePedirDelivery = !!this.restaurantService.restaurant?.info?.modules?.new_home_pedir_delivery;
		// ngOnInit do root é 100% orquestração de navegação/estado do browser
		// (localStorage, navService, router events, cart). No SSR/prerender nada
		// disso roda — o server só renderiza o outlet do cardápio.
		if (!isPlatformBrowser(this.platformId)) return;
		// O Angular montou: o que faltar daqui para a frente é a navegação de
		// entrada, e é essa a fase que o vigia vai reportar se travar.
		vigiaDeBoot.marcarFaseDoBoot('primeira-tela');
		// Frescor deliberado: a aba que fica aberta descobre sozinha que existe
		// build nova, em vez de descobrir quebrando com ChunkLoadError. Só
		// recarrega fora do fluxo de pedido — ver version-check.ts.
		this.versionCheckService.iniciar();
        this.loadingSpinner.dismiss();
		this.router.events.subscribe(
			(event: RouterNavigationEvent) => {
				if (event instanceof NavigationEnd) {
					if (this.activatedRoute.snapshot.queryParamMap['params']['preference_id'] && this.activatedRoute.snapshot.queryParamMap['params']['collection_status'] == "null")
                    this.localStorageService.setItem('external_reference_' + this.activatedRoute.snapshot.queryParamMap['params']['external_reference'], this.activatedRoute.snapshot.queryParamMap['params']['preference_id']);         
                        
					if(this.activatedRoute.snapshot.queryParamMap['params']['cid'] && this.activatedRoute.snapshot.queryParamMap['params']['oid']){
                        this.proceedAfterPayment(this.activatedRoute.snapshot.queryParamMap['params']['oid'], this.activatedRoute.snapshot.queryParamMap['params']['cid']);
                        return;
					}
				}
			}
		);
		
		// Começa a vigiar as navegações do usuário AGORA, antes do `await` do último
		// pedido abaixo: a corrida que este vigia resolve (ver `agendarNavegacaoDeBoot`)
		// abre justamente durante essa espera de rede, e um vigia criado depois dela
		// não veria o clique que já aconteceu.
		const vigia = this.vigiarNavegacaoDoUsuario();

		this.restaurantUrl = this.restaurantService.restaurant ? this.restaurantService.restaurant.info.restaurant_url : "not-found";
		let currentRoute = this.location.path().split('/');

		let routeTo = '/menu';
        let params = null;
        let token = null;
        let asModal = false;

        if(this.restaurantUrl && currentRoute.some(path => path.match(/^track|reset-password|rate-order.*$/))) {            
            currentRoute.splice(1,1)
            routeTo = currentRoute.join('/');
            token = currentRoute[1].split('?token=')[1];
		} else {
            token = this.restaurantService.restaurant ? this.localStorageService.getItem('lastOrderToken-'+this.restaurantService.restaurant.info.id) : null
            if (token) routeTo = 'rate-order';
        }

        let shouldRequestOrder = true;
        let isRatePage = routeTo.includes('rate-order');
        if (isRatePage) shouldRequestOrder = this.restaurantService.restaurant ? this.restaurantService.restaurant.info.review_settings.enabled : false;

        if (token && shouldRequestOrder && !currentRoute.some(path => path.includes('reset-password'))) {
            // PRAZO obrigatório: a navegação de entrada mora DEPOIS deste await.
            // `getCachedOrderByUUID_forTracking` é um `http.get` cru, e o app não
            // tem interceptor de timeout — se a requisição pendurar, o `ngOnInit`
            // nunca chega ao `agendarNavegacaoDeBoot` e o cliente fica numa tela
            // vazia para sempre. Atinge só quem JÁ COMPROU nesta loja (é o que
            // cria o `lastOrderToken`), ou seja, exatamente o cliente recorrente.
            const r = await comPrazo(
                this.orderService.getCachedOrderByUUID_forTracking(token),
                PRAZO_ULTIMO_PEDIDO_MS,
            );
            if (r.estourou) {
                // Sem o último pedido não dá para abrir a avaliação; o cardápio é
                // o destino seguro. Perder a tela de avaliação custa pouco perto
                // de não entregar tela nenhuma.
                routeTo = '/menu';
                params = null;
            } else if (r.erro) {
                console.error(r.erro);
            } else {
                // Bloco preservado do `.then()` original, inclusive o caso em que
                // `response` é nulo e `response.askReview` lança — o `try` abaixo
                // faz o papel que o `.catch()` fazia. Mudar isso aqui seria uma
                // correção diferente, e ela não pertence a esta.
                const response: any = r.valor;
                try {
                    if (!response) routeTo = '/menu';

                    params = {'order': response, 'asModal': asModal, 'caller': 'menu'}
                    if (isRatePage) {
                        if (!response.askReview || response.ratingAvg)
                            params = null;
                        else
                            params.asModal = !currentRoute.some(path => path.includes('rate-order'));
                    }
                } catch (error) {
                    console.error(error);
                }
            }
        }

        // Sem restaurante carregado não há carrinho a recuperar. A guarda não é
        // defensividade genérica: sem ela, o `ngOnInit` MORRE aqui e nunca chega
        // ao `agendarNavegacaoDeBoot` da linha seguinte — o usuário fica preso
        // numa tela vazia em vez de ver o `<app-not-found>` que o menu já sabe
        // mostrar (`menu.page.ts` faz `if(!restaurant) { invalidURL = true }`).
        //
        // Trinta linhas acima o mesmo método já trata `restaurant` ausente
        // (`... ? ... : "not-found"`); esta linha era a única que não tratava.
        // Medido pelo Sentry (PEDIRDELIVERY-8/N), sempre em `/app/` sem slug.
        if (this.restaurantService.restaurant) {
            // `items?.` e não `items.`: carrinho gravado por uma versão antiga
            // (ou corrompido por extensão) pode não ter a chave, e aí o `.length`
            // relança o mesmo problema que a guarda acima acabou de resolver.
            const cart = JSON.parse(localStorage.getItem(`cart-${this.restaurantService.restaurant.info.id}`));
            if (this.cartRecoverService.isRecover && cart?.items?.length > 0)
                routeTo = '/shopping-cart';
        }

		this.agendarNavegacaoDeBoot(routeTo, params, vigia);
            this.idleService.initializeCounter();
        }

	/**
	 * Marca se o usuário navegou por conta própria durante o boot.
	 *
	 * Duas condições, e as duas vieram de comportamento medido, não de suposição:
	 *
	 *  - só depois do primeiro `NavigationEnd`. Até ele, o que roda é a navegação
	 *    INICIAL, e ela nem sempre é uma navegação só: num deep link de `/track` ou
	 *    `/rate-order` o `DataGuardService` rejeita a rota (ainda não há `navService.data`)
	 *    e redireciona para a raiz, emitindo um segundo `NavigationStart` — que não é
	 *    gesto de ninguém. O usuário, por sua vez, só consegue clicar depois que a tela
	 *    montou, isto é, depois desse primeiro `NavigationEnd`;
	 *
	 *  - e só para uma rota DIFERENTE da de entrada, porque navegar para onde o app já
	 *    está não tira ninguém do lugar.
	 *
	 * Frouxo demais, a tela de entrada nunca seria empurrada e o cliente que acabou de
	 * pedir não cairia na avaliação; apertado demais, o boot volta a expulsar quem clicou.
	 */
	private vigiarNavegacaoDoUsuario() {
		const normalizar = (url: string) => (url || '').split('?')[0].replace(/\/+$/, '');
		const entrada = normalizar(this.location.path());

		this.houveNavegacaoDoUsuario = false;
		let navegacaoInicialTerminou = false;

		return this.router.events.subscribe((e: RouterNavigationEvent) => {
			if (e instanceof NavigationEnd) { navegacaoInicialTerminou = true; return; }
			if (e instanceof NavigationStart && navegacaoInicialTerminou && normalizar(e.url) !== entrada) {
				this.houveNavegacaoDoUsuario = true;
			}
		});
	}

	/**
	 * Navegação final do boot — leva o app à tela de entrada (`/menu`, ou `rate-order`
	 * / `/shopping-cart` quando há último pedido ou carrinho a recuperar).
	 *
	 * Ela chega ATRASADA de propósito (o timer abaixo), e mais ainda quando o `await`
	 * do último pedido, no `ngOnInit`, espera a rede. Se nesse meio-tempo o usuário já
	 * navegou — clicou num item do cardápio, abriu o carrinho —, empurrar `routeTo` o
	 * EXPULSA da tela em que está, de volta para o menu. É corrida real, não teórica:
	 * o E2E do construtor de pizza morria assim ("btnFlavorsQuantity never found" com
	 * a tela já de volta no menu — ver `cypress/integration/orderFlow`), e em produção
	 * atinge quem clica rápido. A janela é maior justamente para o cliente RECORRENTE:
	 * ele tem `lastOrderToken` e paga o tempo da requisição antes de o timer começar.
	 *
	 * Por isso o vigia é armado lá em cima, no início do `ngOnInit`, e só chega aqui
	 * para ser consultado e desligado: a intenção do usuário vence a do boot.
	 */
	private agendarNavegacaoDeBoot(routeTo: string, params: any, vigia?: Subscription, atrasoMs = 500) {
		setTimeout(() => {
			if (vigia) vigia.unsubscribe();
			// Os dois caminhos desarmam o vigia de boot, e por motivos diferentes:
			// no primeiro o usuário já navegou sozinho (logo, viu tela), no
			// segundo o boot entregou a dele. Desarmar só num deles produziria
			// um "travou" falso justamente para quem clicou rápido.
			if (this.houveNavegacaoDoUsuario) {
				vigiaDeBoot.marcarBootPronto();
				return this.loadingSpinner.dismiss();
			}
			// `catch` de última instância: `navegarNoBoot` já trata tudo por dentro,
			// mas uma promessa solta que rejeitasse aqui viraria `unhandledrejection`
			// no boot de todo cliente.
			this.navegarNoBoot(routeTo, params).catch(() => undefined);
		}, atrasoMs);
	}

	/**
	 * A navegação de entrada propriamente dita — COM PRAZO E COM PLANO B.
	 *
	 * Até 01/09/2026 esta chamada era um `.then()` solto: sem `.catch()`, sem
	 * prazo. Os três desfechos que ela não tratava terminavam do mesmo jeito para
	 * o cliente — tela em branco permanente, porque o spinner já tinha sido
	 * dispensado lá no início do `ngOnInit` (a linha `loadingSpinner.dismiss()`
	 * antes do `subscribe` do router):
	 *
	 *   - a promessa NÃO ASSENTA  → o `.then` nunca corre (~334 devices/dia,
	 *                               em silêncio absoluto — ver `PRAZO_NAVEGACAO_BOOT_MS`);
	 *   - a promessa REJEITA      → `unhandledrejection`, e o boot para ali;
	 *   - resolve com `false`     → guard barrou (já era tratado, ver abaixo).
	 *
	 * O prazo é o único remédio para o primeiro: `catch` não cobre promessa que
	 * não assenta, ela não rejeita — simplesmente não volta. Por isso `comPrazo`,
	 * o mesmo helper que o `await` do último pedido já usa neste arquivo.
	 */
	private async navegarNoBoot(routeTo: string, params: any): Promise<void> {
		try {
			// A partir daqui a promessa está EM VOO. A fase é o que separa "montou e
			// nem chegou a pedir navegação" de "pediu e a navegação pendurou" — duas
			// causas diferentes que hoje chegam ao Athena como a mesma
			// `'primeira-tela'` e por isso não têm medição nenhuma.
			this.avisarVigiaDaNavegacao(routeTo);

			const r = await comPrazo(this.navService.push(routeTo, params), PRAZO_NAVEGACAO_BOOT_MS);

			// Estouro e rejeição pedem investigações DIFERENTES e por isso não podem
			// chegar ao dataset com a mesma cara: rejeição é o chunk da rota que não
			// baixou (`ChunkLoadError` no lazy load) ou guard/resolver que lançou —
			// tem mensagem, tem pilha, tem série própria no Sentry; estouro é a
			// promessa que nunca assenta, e a ausência de mensagem É o sinal.
			// O tratamento é o mesmo (entregar tela); a telemetria, não.
			// `!== undefined` e não a checagem de verdade: `Promise.reject(undefined)`
			// e `reject(false)` existem, e cair no caminho de sucesso com eles seria
			// tratar uma navegação que falhou como se tivesse acontecido.
			if (r.estourou || r.erro !== undefined) {
				return this.recuperarNavegacaoDeBoot(routeTo, r.estourou ? 'prazo' : 'rejeicao', r.erro);
			}

			// O BOOLEANO IMPORTA. `navigateByUrl` RESOLVE com `false` quando um
			// guard barra a rota — não rejeita. Ignorá-lo desarmava o vigia por
			// uma navegação que não aconteceu, e a partir daí um travamento
			// deixava de ser medido.
			//
			// Hoje isso não deixa ninguém preso: os quatro guards do app navegam
			// antes de recusar (`data-guard`/`cart-guard` fazem `setRoot('')`,
			// `auth-guard` manda para /login, `route-not-found` devolve UrlTree),
			// então o cliente vai para a raiz. Mas quem confia nisso está
			// confiando num redirect que este código não observa — e o precedente
			// existe: o `parseURL` lançando DENTRO do `setRoot` era o MP-569.
			//
			// Basta alguém escrever um guard com `false` puro — o padrão que o
			// próprio Angular recomenda evitar — para o cliente ficar parado E
			// invisível ao mesmo tempo. Por isso `false` NÃO marca pronto: o vigia
			// segue armado e o travamento continua mensurável.
			// `any` porque `comPrazo` não consegue inferir T (a assinatura aceita
			// `Promise<T> | any`); é o mesmo que o `await` do último pedido faz.
			const navegou: any = r.valor;
			if (navegou !== false) vigiaDeBoot.marcarBootPronto();
			this.loadingSpinner.dismiss();
		} catch (e) {
			// Nada pode escapar daqui. Uma exceção neste ponto devolveria o cliente
			// ao estado exato que esta correção veio consertar: spinner já
			// dispensado e nenhuma tela no lugar.
			this.recuperarNavegacaoDeBoot(routeTo, 'rejeicao', e);
		}
	}

	/**
	 * Plano B: a navegação de entrada não voltou (ou voltou rejeitando).
	 *
	 * O objetivo aqui é ENTREGAR TELA, mesmo que a errada. Um cliente no cardápio
	 * de uma loja que ele não pediu ainda tem saída; um cliente numa tela branca
	 * não tem nenhuma, e é o que ele relata como "o app não abre".
	 */
	private recuperarNavegacaoDeBoot(routeTo: string, motivo: 'prazo' | 'rejeicao', erro?: any): void {
		const alvoEraCardapio = this.ehRotaDoCardapio(routeTo);

		this.motivoDoPlanoB = motivo;
		this.reportarPlanoBDoBoot(routeTo, motivo, alvoEraCardapio ? 'nenhum' : ROTA_SEGURA_DE_BOOT, erro);

		// Re-navegar para o cardápio quando o alvo JÁ era o cardápio seria repetir a
		// navegação que acabou de falhar: no melhor caso não muda nada, no pior
		// enfileira uma segunda promessa pendurada em cima da primeira.
		//
		// O que NÃO se pode fazer é o que este ramo fazia até 02/09/2026: não
		// navegar E dar o boot por encerrado, apoiado em "a tela do menu já está
		// montada". Se o `push('/menu')` pendurou, ela não está — o outlet está
		// vazio, o spinner acabou de sair e o vigia acabou de ser desarmado. Era
		// tela branca sem spinner e sem alarme, em 3 dos 6 primeiros casos reais.
		// Quem resolve isso agora é `verificarSeEntregouTela`, logo abaixo, e ela
		// vale para os DOIS ramos: a navegação do plano B também pode não chegar.
		//
		// E `location.reload()` aqui seria pior ainda: este código roda em TODO
		// boot, então falha persistente (chunk podado do bucket, rede morta,
		// service worker servindo build fóssil) viraria loop infinito de recarga —
		// trocaria uma tela branca por um aparelho girando sozinho. O reload existe
		// na tela de recuperação, atrás de um GESTO do cliente: quem clica não
		// entra em loop.
		if (!alvoEraCardapio) {
			// Fire-and-forget de propósito: sem prazo e sem `then`. O spinner sai e o
			// boot é marcado logo abaixo, então esperar de novo só recriaria a espera
			// sem fim que estamos consertando. O `catch` existe só para a rejeição
			// não virar `unhandledrejection`; `Promise.resolve` normaliza o caso de
			// `push` devolver algo que não é promessa.
			try {
				Promise.resolve(this.navService.push(ROTA_SEGURA_DE_BOOT, null)).catch(() => undefined);
			} catch (e) { /* sem navegação possível: resta o que já está montado */ }
		}

		// A rede de segurança dos dois ramos: daqui a pouco, se NADA tiver sido
		// renderizado, o app desenha a própria tela. Agendado antes de marcar
		// pronto porque marcar pronto é o que desarma o vigia — a partir da linha
		// de baixo, este agendamento é a única coisa que ainda olha por este
		// cliente.
		this.agendarUltimaChance();

		// Os dois desfechos terminam igual — o cliente NÃO pode ficar preso.
		//
		// Marcar pronto desarma o vigia, e isso é deliberado: o boot terminou,
		// ainda que pelo plano B, e quem conta este desfecho agora é o
		// `ClientBootNavFallback`. Consequência para quem lê o painel: parte da
		// série `ClientBootStalled` (fase `'primeira-tela'`) MIGRA para o passo
		// novo a partir desta build — ela não caiu, mudou de nome.
		vigiaDeBoot.marcarBootPronto();
		this.loadingSpinner.dismiss();
	}

	/**
	 * Agenda a verificação de "sobrou alguma tela?".
	 *
	 * `setTimeout` e não `Promise`: o que se espera aqui é tempo de relógio, não
	 * uma promessa — e a promessa que havia para esperar é justamente a que não
	 * assenta. Fora de `try` nada: um defeito no agendamento não pode derrubar o
	 * boot que o plano B acabou de entregar.
	 */
	private agendarUltimaChance(): void {
		try {
			setTimeout(() => this.verificarSeEntregouTela(), PRAZO_ULTIMA_CHANCE_MS);
		} catch (e) { /* ambiente sem timer: resta o que já está montado */ }
	}

	/**
	 * Passou o prazo. Alguma navegação COMPLETOU? Se não, desenha tela.
	 *
	 * `router.navigated` é a régua certa, e não uma inspeção do DOM: o Angular o
	 * põe em `true` na primeira navegação que termina, e ele responde exatamente
	 * a pergunta que interessa — "o outlet chegou a receber alguma coisa?".
	 * Contar filhos de `<ion-app>` seria heurística, e heurística de DOM erra
	 * justamente nos motores estranhos que compõem esta população.
	 */
	private verificarSeEntregouTela(): void {
		try {
			// `=== false`, NUNCA `!`: num roteador de teste (ou num mock parcial) a
			// propriedade vem `undefined`, e `!undefined` é `true` — a dúvida viraria
			// uma tela de erro por cima de um app que subiu normalmente. Só o `false`
			// explícito do Angular, que significa "nenhuma navegação completou desde
			// o boot", liga o bloco. Na dúvida, não piorar.
			if (this.router.navigated !== false) return;
			if (this.bootSemTela) return;

			this.bootSemTela = true;
			this.removerTelaDeRecuperacaoQuandoAlgoMontar();

			// Segundo evento do MESMO passo, de propósito: o primeiro (em 10 s) diz
			// que a navegação falhou, este diz que a recuperação de primeira linha
			// também não bastou. A razão entre os dois é o que mede se o plano B
			// entrega tela de verdade. Consequência para quem lê o painel: um device
			// pode gerar DOIS `ClientBootNavFallback` — contar por `device_id`.
			this.reportarPlanoBDoBoot(
				vigiaDeBoot.rotaDeBoot(), this.motivoDoPlanoB, 'tela-recuperacao',
				undefined, PRAZO_NAVEGACAO_BOOT_MS + PRAZO_ULTIMA_CHANCE_MS);
		} catch (e) { /* telemetria/estado nunca derruba o que já está na tela */ }
	}

	/**
	 * Enquanto o aviso estiver na tela, a primeira navegação que COMPLETAR o tira.
	 *
	 * Existe porque `bootSemTela` só sabia virar `true`. O bloco é
	 * `position: fixed` cobrindo a viewport inteira, então, sem este caminho de
	 * volta, um app que sobe ATRASADO fica renderizado embaixo de um aviso de
	 * erro permanente — pior que o problema que o aviso veio resolver, porque o
	 * cardápio está ali, funcionando, e o cliente não alcança.
	 *
	 * Não é hipótese. Medido em produção em 03/09/2026, nas primeiras cinco horas
	 * do deploy: dos 6 devices que chegaram ao `destino: 'tela-recuperacao'`,
	 * 5 emitiram `ClientMenuViewed` de 1 a 10 s depois — com o MESMO
	 * `correlation_id`, ou seja, SEM recarregar. O cardápio montou embaixo do
	 * aviso.
	 *
	 * Por que assinar aqui, e não esticar o prazo: dois dos casos medidos levaram
	 * 172 s e 406 s, e o prazo não pode crescer — ele está preso abaixo do vigia
	 * (20 s) por invariante. Só a observação cobre a cauda inteira.
	 *
	 * Por que a assinatura nasce SÓ neste ponto: fora do aviso ela não teria o
	 * que fazer, e o `ngOnInit` já tem uma assinatura de `NavigationEnd` cheia de
	 * `return` — pendurar ali é depender de nenhum deles vir antes.
	 */
	private removerTelaDeRecuperacaoQuandoAlgoMontar(): void {
		try {
			// `first`/`take(1)` seriam o idiomático, mas o operador teria de ser
			// importado num arquivo que hoje só usa `Subscription`; a desinscrição
			// explícita faz o mesmo e não some se alguém mexer nos imports.
			const assinatura: Subscription = this.router.events.subscribe(
				(evento: RouterNavigationEvent) => {
					try {
						if (!(evento instanceof NavigationEnd)) return;
						this.bootSemTela = false;
						assinatura.unsubscribe();
					} catch (e) { /* nunca derrubar a navegação por causa do aviso */ }
				});
		} catch (e) { /* sem roteador observável: o aviso fica, e o botão resolve */ }
	}

	/**
	 * Recarrega — e só por GESTO do cliente.
	 *
	 * É a diferença entre isto e o `location.reload()` automático que o plano B
	 * recusa: falha persistente (chunk podado, rede morta, worker servindo build
	 * fóssil) faz o reload automático virar loop infinito, porque o código roda em
	 * TODO boot. Atrás de um clique, não: quem clica duas vezes e não vê tela para
	 * de clicar. O custo de errar aqui é uma recarga a mais, não um aparelho
	 * girando sozinho.
	 */
	public recarregarPorGesto(): void {
		try {
			if (typeof window !== 'undefined' && window.location) window.location.reload();
		} catch (e) { /* sem window: não há o que recarregar */ }
	}

	/**
	 * Avisa o vigia que a navegação de entrada foi PEDIDA, e para onde.
	 *
	 * Tudo dentro de `try`: o vigia é INSTRUMENTO, e um defeito nele não pode ser
	 * o motivo de o app não subir — seria o instrumento causando a doença que veio
	 * medir. É a mesma regra que o próprio `boot-watchdog.ts` se impõe.
	 *
	 * A rota vai normalizada (sem query string, cortada em 40) antes de sair
	 * daqui: o deep link de `/track` e `/rate-order` carrega `?token=…`, e token é
	 * PII. O `marcarRotaDeBoot` sanitiza de novo do lado dele — as duas pontas
	 * fazem, de propósito, porque quem lê o payload não deve precisar confiar em
	 * quem o chamou.
	 */
	private avisarVigiaDaNavegacao(routeTo: string): void {
		try {
			vigiaDeBoot.marcarRotaDeBoot(this.normalizarRota(routeTo).slice(0, 40));
			vigiaDeBoot.marcarFaseDoBoot('navegacao-pedida');
		} catch (e) { /* instrumento nunca derruba o boot */ }
	}

	/**
	 * Registra o desfecho do plano B.
	 *
	 * Passo PRÓPRIO (`ClientBootNavFallback`) e não um campo do
	 * `ClientBootStalled`: o vigia é desarmado logo abaixo, então ele não
	 * reportaria mais nada — sem este evento a correção ficaria sem instrumento
	 * e a série de travamento simplesmente cairia, sem ninguém saber para onde.
	 *
	 * `emitHealth` e não `emit` por dois motivos, os dois medidos: o gate de saúde
	 * aceita `restaurant_id = 0` (é o único que enxerga falha antes de a loja
	 * carregar — e aqui a loja pode nem ter carregado), e o transporte é
	 * `sendBeacon`, que sobrevive ao fechamento da aba. Depois de 10 s de tela
	 * branca, fechar a aba é exatamente o que o cliente faz.
	 */
	private reportarPlanoBDoBoot(routeTo: string, motivo: string, destino: string, erro?: any,
								ms: number = PRAZO_NAVEGACAO_BOOT_MS): void {
		try {
			// Em teste unitário o componente é construído por posição e pode vir sem
			// o emissor; isso não pode derrubar o plano B, que é o que entrega tela.
			if (!this.orderFlowTrace) return;
			this.orderFlowTrace.emitHealth('ClientBootNavFallback', {
				motivo,
				// A rota vem do VIGIA, não de uma sanitização local: os dois passos
				// (`ClientBootStalled` e este) precisam gravar a string IDÊNTICA para
				// se cruzarem por `rota_alvo` no Athena. Duas normalizações
				// parecidas — uma com barra inicial, outra sem — dariam duas colunas
				// que nunca casam, e o join sairia vazio sem ninguém notar.
				rota_alvo: vigiaDeBoot.rotaDeBoot(),
				destino,
				// O PRAZO que venceu até aqui, não o tempo medido: 10 s no evento do
				// plano B, 13 s no da tela de recuperação. Quem quiser o decorrido
				// real usa o `ms_nav` do `ClientBootStalled` ou o delta de `ts` entre
				// os eventos irmãos do mesmo `correlation_id` — este campo é o
				// orçamento estourado, e é assim que ele deve ser lido.
				ms,
				// A CLASSE do erro é o que discrimina, e ela não é texto do cliente:
				// 'ChunkLoadError' (chunk lazy que não baixou) e 'HttpErrorResponse'
				// (resolver que falhou) pedem investigações diferentes, e os dois
				// nomes cabem numa coluna sem redação.
				erro_classe: motivo === 'rejeicao' ? this.classeDoErro(erro) : '',
				// A MESMA string nos dois campos abaixo, de propósito — é o padrão do
				// `ClientAppError` (ver `order-flow-error-handler.ts`): a allowlist
				// redige cada um de um jeito, `str(N)` e hash de 8 hex. O emissor não
				// calcula hash nenhum.
				//
				// Por que a mensagem NÃO vai crua: um `HttpErrorResponse` traz a URL
				// pedida na `message`, e deep link de `/track` e `/rate-order` carrega
				// `?token=`. O token é do cliente. Perde-se ler a mensagem; não se
				// perde diagnóstico, porque `erro_classe` diz a classe e `erro_hash`
				// agrupa as ocorrências iguais.
				//
				// Só a rejeição tem mensagem. No estouro não há nada a contar, e é
				// justamente essa ausência que distingue as duas causas no Athena.
				erro_msg: motivo === 'rejeicao' ? this.mensagemDoErro(erro) : '',
				erro_hash: motivo === 'rejeicao' ? this.mensagemDoErro(erro) : '',
			}, 'app.component.recuperarNavegacaoDeBoot');
		} catch (e) { /* telemetria nunca derruba o boot */ }
	}

	/**
	 * Classe do erro ('ChunkLoadError', 'HttpErrorResponse', 'TypeError').
	 *
	 * É o campo de diagnóstico que sobrou depois de a mensagem virar `str(N)`: o
	 * nome vem do nosso runtime, é vocabulário fechado e não tem como carregar
	 * dado do cliente. Cai para 'sem-nome' em vez de sumir, porque a coluna vazia
	 * já significa outra coisa aqui (motivo='prazo', que não tem erro nenhum).
	 */
	private classeDoErro(erro: any): string {
		try {
			const n = erro && erro.name;
			return n ? String(n).slice(0, 40) : 'sem-nome';
		} catch (e) {
			return 'sem-nome';
		}
	}

	/** Mensagem enxuta do erro. Nunca lança e nunca leva objeto do cliente adiante. */
	private mensagemDoErro(erro: any): string {
		try {
			const m = (erro && erro.message) ? erro.message : erro;
			return (m === undefined || m === null) ? '' : String(m).slice(0, 160);
		} catch (e) {
			return '';
		}
	}

	/**
	 * A rota alvo já era o cardápio?
	 *
	 * Precisa normalizar porque `routeTo` chega em três formas neste arquivo:
	 * `'/menu'` (default e fallback do último pedido), `'rate-order'` (sem barra)
	 * e o caminho remontado do deep link (`'/track?token=…'`). Comparação crua
	 * daria `false` para `'menu'` contra `'/menu'` e o plano B re-navegaria em
	 * cima de si mesmo.
	 *
	 * Rota vazia conta como cardápio: `NavService.parseURL('')` cai em
	 * `ROTA_SEM_RESTAURANTE`, que é `'menu'`.
	 */
	private ehRotaDoCardapio(routeTo: string): boolean {
		const rota = this.normalizarRota(routeTo);
		return rota === '' || rota === 'menu';
	}

	/** Caminho sem barra inicial/final, sem query e sem fragmento. Nunca lança. */
	private normalizarRota(routeTo: string): string {
		try {
			return String(routeTo || '')
				.split('?')[0].split('#')[0]
				.replace(/^\/+/, '').replace(/\/+$/, '');
		} catch (e) {
			return '';
		}
	}

    proceedAfterPayment(orderID, clientID) {
        this.orderService.getOrderByOrderAndClientID(orderID, clientID, this.restaurantService.restaurant.info.id).then((response) => {
            this.afterPaymentService.open(response);
        }).catch((error) => {
            console.error(error);
        });
    }
}